import React, { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Upload, CheckCircle2, Clock, Heart, Loader2, XCircle } from "lucide-react";
import { api, fmt, errMsg, getToken, fmtRange } from "@/lib/api";

export default function BookingStatus() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const { data: b, refetch } = useQuery({
    queryKey: ["pub-booking", id],
    queryFn: async () => (await api.get(`/public/bookings/${id}`)).data,
    refetchInterval: 6000,
  });

  const copyNumber = () => {
    navigator.clipboard.writeText((b?.transfer?.transfer_card_number || "").replace(/\s/g, ""));
    toast.success("تم نسخ رقم البطاقة");
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/public/bookings/${id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تم رفع وصل التحويل");
      refetch();
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setUploading(false); }
  };

  if (!b) return <div className="min-h-screen gradient-navy flex items-center justify-center text-gold font-display text-2xl">جارٍ التحميل...</div>;

  return (
    <div className="min-h-screen gradient-navy text-offwhite px-5 py-10">
      <div className="max-w-xl mx-auto">
        <p className="text-center tracking-[0.4em] text-gold text-sm mb-8" onClick={() => navigate("/")}>QAMARIA</p>

        {b.status === "pending" && (
          <Panel data-testid="status-pending">
            <Icon color="gold"><Clock size={40} /></Icon>
            <span className="inline-block text-xs px-3 py-1.5 rounded-full bg-gold/15 text-gold mb-4" data-testid="pending-chip">طلبك قيد الانتظار — بانتظار تأكيد قمرية</span>
            <h1 className="font-display text-3xl mb-3">تم استلام طلبك بنجاح 🤍</h1>
            <p className="text-muted2 leading-relaxed mb-3">سنراجع طلبك ونتواصل معك قريبًا لإتمام التفاصيل.</p>
            <p className="text-offwhite/90 text-sm leading-relaxed bg-navy p-3 rounded-xl text-start">
              <span className="text-gold font-semibold">مهم:</span> لم يتم تثبيت حجزك بعد، ولم يُدفع العربون. يبقى التاريخ متاحًا حتى دفع العربون وتأكيده من إدارة قمرية.
            </p>
            <Stepper active={0} />
            <Summary b={b} />
          </Panel>
        )}

        {(b.status === "approved" || b.status === "receipt_uploaded") && (
          <Panel data-testid="status-approved">
            <span className="inline-block text-xs px-3 py-1.5 rounded-full bg-gold/15 text-gold mb-4" data-testid="approved-chip">{b.status === "receipt_uploaded" ? "بانتظار تأكيد التحويل" : "بانتظار دفع العربون"}</span>
            <h1 className="font-display text-3xl mb-2">تثبيت الحجز</h1>
            <p className="text-muted2 mb-6">لتثبيت موعدك، يرجى تحويل العربون ورفع صورة الوصل.</p>
            <div className="card-lux p-5 mb-5 text-center">
              <p className="text-sm text-muted2">العربون المطلوب</p>
              <p className="gold-text font-bold text-3xl my-1" data-testid="deposit-amount">{fmt(b.deposit_required)}</p>
            </div>
            <div className="card-lux p-5 mb-5 text-start space-y-3">
              <p className="text-gold font-semibold">حوّل العربون إلى بطاقة قمرية</p>
              <Row k="اسم المصرف" v={b.transfer?.transfer_bank} />
              <Row k="اسم الحساب" v={b.transfer?.transfer_account_name} />
              <div>
                <p className="text-xs text-muted2 mb-1">رقم البطاقة / الحساب</p>
                <div className="flex items-center justify-between gap-3 bg-navy p-3 rounded-xl">
                  <span className="font-mono text-lg tracking-wider" dir="ltr" data-testid="card-number">{b.transfer?.transfer_card_number}</span>
                  <button onClick={copyNumber} data-testid="copy-number-btn" className="btn-gold px-4 py-2 rounded-full text-sm flex items-center gap-1.5 shrink-0">
                    <Copy size={15} /> نسخ الرقم
                  </button>
                </div>
              </div>
              {b.transfer?.transfer_instructions && <p className="text-xs text-muted2 leading-relaxed">{b.transfer.transfer_instructions}</p>}
            </div>
            {b.status === "receipt_uploaded" ? (
              <div className="card-lux p-5 text-center" data-testid="receipt-pending">
                <Icon color="gold"><Clock size={32} /></Icon>
                <p className="text-offwhite font-medium">تم استلام وصل التحويل وهو الآن قيد المراجعة.</p>
                <button onClick={() => fileRef.current.click()} className="text-gold text-sm mt-3 underline">رفع وصل آخر</button>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted2 mb-3 text-center">بعد التحويل، ارفع صورة وصل التحويل</p>
                <button onClick={() => fileRef.current.click()} disabled={uploading} data-testid="upload-receipt-btn"
                  className="btn-gold w-full py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-50">
                  {uploading ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />} رفع وصل التحويل
                </button>
              </>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="receipt-file-input" />
          </Panel>
        )}

        {b.status === "confirmed" && (
          <Panel data-testid="status-confirmed">
            <Icon color="green"><Heart size={40} className="text-gold" fill="#D4AF37" /></Icon>
            <h1 className="font-display text-3xl mb-2">موعدكم أصبح لنا 🤍</h1>
            <p className="text-muted2 leading-relaxed mb-6">تم تثبيت حجزكم في قمرية بنجاح، ونتطلع لأن نصنع معكم ليلة استثنائية.</p>
            <div className="card-lux p-5 space-y-3 text-start">
              <Row k="المكان" v={b.venue_name} />
              <Row k="التاريخ" v={b.date} />
              <Row k="الوقت" v={fmtRange(b.start_time, b.end_time)} />
              <Row k="الباقة" v={b.package_snapshot?.name} />
              <div className="gold-divider my-1" />
              <Row k="المبلغ الكلي" v={fmt(b.final_price)} gold />
              <Row k="العربون المدفوع" v={fmt(b.deposit_paid)} />
              <Row k="المبلغ المتبقي" v={fmt(b.remaining)} />
              <div className="gold-divider my-1" />
              <Row k="رقم الحجز" v={b.booking_number} gold />
            </div>
          </Panel>
        )}

        {b.status === "cancelled" && (
          <Panel data-testid="status-cancelled">
            <Icon color="red"><XCircle size={40} className="text-red-400" /></Icon>
            <h1 className="font-display text-3xl mb-2">تم إلغاء هذا الحجز</h1>
            <p className="text-muted2">للاستفسار يرجى التواصل مع إدارة قمرية.</p>
          </Panel>
        )}
      </div>
    </div>
  );
}

const Panel = ({ children, ...p }) => <div className="card-lux p-7 text-center animate-fade-up" {...p}>{children}</div>;
const STEP_LABELS = ["استلام الطلب", "موافقة قمرية", "دفع العربون", "تأكيد الحجز"];
const Stepper = ({ active }) => (
  <div className="flex items-center justify-between mt-6" data-testid="status-stepper">
    {STEP_LABELS.map((s, i) => (
      <React.Fragment key={s}>
        <div className="flex flex-col items-center gap-1">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs shrink-0 ${i <= active ? "bg-gold text-navy font-bold" : "bg-navy3 text-muted2"}`}>{i < active ? "✓" : i + 1}</div>
          <span className={`text-[9px] whitespace-nowrap ${i <= active ? "text-gold" : "text-muted2"}`}>{s}</span>
        </div>
        {i < STEP_LABELS.length - 1 && <div className={`h-0.5 flex-1 mx-1 mb-4 ${i < active ? "bg-gold" : "bg-navy3"}`} />}
      </React.Fragment>
    ))}
  </div>
);
const Icon = ({ children, color }) => (
  <div className={`w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center ${color === "gold" ? "bg-gold/15 text-gold" : color === "green" ? "bg-green-500/15" : "bg-red-500/15"}`}>{children}</div>
);
const Row = ({ k, v, gold }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted2">{k}</span>
    <span className={gold ? "gold-text font-bold" : "text-offwhite font-medium"}>{v}</span>
  </div>
);
const Summary = ({ b }) => (
  <div className="card-lux p-4 mt-6 space-y-2 text-start">
    <Row k="المكان" v={b.venue_name} />
    <Row k="التاريخ" v={b.date} />
    <Row k="الوقت" v={fmtRange(b.start_time, b.end_time)} />
    <Row k="الباقة" v={b.package_snapshot?.name} />
    <Row k="المجموع التقديري" v={fmt(b.base_total)} gold />
  </div>
);
